import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import { extractAuth0UserInfo, getPreferredEmail } from '../utils/auth0EmailUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { changeLanguage } from '../../i18n/i18n';
import { fetchUserProfile } from '../../store/actions/DataAction';
import { readErrorMessage } from '../../utils/errorMessage';
import { hydrateUserPreferences } from '../../store/actions/SettingsActions';
import { Account, loadAccount } from '../../services/api/accountService';

interface Auth0ProfileResponse {
  success: boolean;
  user: {
    id: string;
    auth_id: string;
    email: string;
    first_name: string;
    last_name: string;
    uuid: string;
    organization_id?: string | null;
    role?: string | null;
    phone?: string | null;
    is_active: boolean;
    is_org_admin: boolean;
    org_type?: string | null;
    onboarding_completed: boolean;
    preferences?: Record<string, any>;
    tour_completions?: Record<string, string>;
    last_login_at?: string;
    created_at: string;
    updated_at: string;
  };
  pubsub?: {
    endpoint: string;
    token: string;
    expires_in: number;
    hubs: string[];
  };
}

/**
 * Session-aware Auth0 profile hook.
 *
 * Flow on page load / refresh:
 * 1. Wait for Auth0 SDK to authenticate (silent token refresh)
 * 2. GET /auth/session-check - lightweight check if the HttpOnly session cookie is still valid
 *    a. If VALID  → skip /login, go straight to GET /auth/profile
 *    b. If EXPIRED → the user was idle past the session lifetime (12 hours):
 *       - If this is a return from a previous session (sessionStorage flag), force logout
 *       - Otherwise (fresh Auth0 login), create a new session via POST /auth/login
 *    c. If MISSING → first-time login, POST /auth/login to create session
 * 3. GET /auth/profile - fetch full user data + PubSub credentials
 * 4. Store everything in Redux
 */
export const useAuth0Profile = () => {
  const { isAuthenticated, isLoading, user: auth0User, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<Auth0ProfileResponse | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const hasAttemptedFetchRef = useRef(false);
  const pubsubRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doLogout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || isLoading || hasAttemptedFetchRef.current) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        hasAttemptedFetchRef.current = true;

        // Ensure Auth0 access token is available (triggers silent refresh if needed)
        try {
          await getAccessTokenSilently();
        } catch (tokenErr) {
          // Token could not be refreshed. This message is shown to the user on
          // the AppShell error screen, so keep it friendly and point them at the
          // "Try Again" button there (which clears cached tokens and reloads).
          throw new Error('Your session needs to refresh. Please click "Try Again" below to continue.');
        }

        // Step 1: Check if we already have a valid session (from previous page load)
        let hasValidSession = false;
        let sessionExpired = false;

        try {
          const sessionCheck = await apis.get('/auth/session-check');
          hasValidSession = sessionCheck.data?.valid === true;
        } catch (checkErr: any) {
          const reason = checkErr.response?.data?.reason;
          if (reason === 'session_expired') {
            sessionExpired = true;
          }
          // 'no_session' or 'error' → hasValidSession stays false
        }

        // Step 2: Handle expired session from idle timeout
        if (sessionExpired) {
          // Check if the user had a previous active session (not a fresh Auth0 login)
          const hadPreviousSession = sessionStorage.getItem('pons_session_active') === 'true';

          if (hadPreviousSession) {
            // User was previously active but idle past the 12-hour session lifetime
            // Force logout so they re-authenticate properly
            setError('Your session has expired due to inactivity. Please log in again.');
            setTimeout(() => doLogout(), 1500);
            return;
          }
          // If no previous session flag, this is a fresh Auth0 login after
          // a previous expired session - allow creating a new one below
        }

        // Step 3: Create session only if we don't have a valid one
        if (!hasValidSession) {
          try {
            const deviceInfo = {
              device_name: `${navigator.userAgent.split(' ').pop() || 'Unknown'}`,
              device_type: 'browser'
            };
            const loginResponse = await apis.post('/auth/login', deviceInfo);

            if (!loginResponse.data.success) {
              throw new Error('Login response unsuccessful');
            }

            if (loginResponse.data.session_reused) {
              // Session was found valid server-side despite session-check failure
              // This can happen in rare race conditions - that's fine
            }
          } catch (loginErr: any) {
            throw new Error(`Click to try again.`);
          }
        }

        // Mark that we now have an active session (for idle-expiry detection on next reload)
        sessionStorage.setItem('pons_session_active', 'true');

        // Step 4: Fetch full profile data
        const response = await apis.get<Auth0ProfileResponse>('/auth/profile');

        if (response.data.success && response.data.user) {
          const user = response.data.user;
          const pubsub = response.data.pubsub;

          const preferredEmail = getPreferredEmail(auth0User, user.email);
          setProfileData(response.data);

          dispatch({
            type: 'STORE_USER_INFO',
            payload: {
              UserID: user.id,
              UserUID: user.uuid,
              auth_id: user.auth_id,
              Email: preferredEmail || user.email,
              FirstName: user.first_name,
              LastName: user.last_name,
              PhoneNumber: user.phone,
              organization_id: user.organization_id,
              UserType: user.role,
              is_active: user.is_active,
              is_org_admin: user.is_org_admin,
              org_type: user.org_type,
              onboarding_completed: user.onboarding_completed,
              preferences: user.preferences,
              created_at: user.created_at,
              updated_at: user.updated_at,
            },
          });

          // Load tour completions into Redux
          dispatch({
            type: 'LOAD_TOUR_COMPLETIONS',
            payload: user.tour_completions || {},
          });

          // Apply user's language preference immediately (before UI renders)
          const userLanguage = user.preferences?.general_settings?.language;
          if (userLanguage) {
            const currentLang = localStorage.getItem('language') || 'en';
            if (userLanguage !== currentLang) {
              await changeLanguage(userLanguage);
            }
          }

          // Before anything that assumes a workspace account.
          const loadedAccount = await loadAccount();
          setAccount(loadedAccount);
          const isWorkspace = loadedAccount?.account_type?.shell !== 'client';

          // Fetch full user profile (includes profile_picture) for Sidebar/Header
          dispatch(fetchUserProfile() as any);

          // Make sure the AI preferences section is in the store from the very
          // first authenticated moment. Idempotent - when /auth/profile already
          // carried `ai_settings` (the common case) this makes no request, it
          // just marks hydration done so the selectors stop showing a loader.
          // Client accounts have no AI settings (and no access to the route).
          if (isWorkspace) dispatch(hydrateUserPreferences() as any);

          // Store PubSub credentials in Redux
          if (pubsub) {
            dispatch({
              type: 'STORE_PUBSUB_CREDENTIALS',
              payload: pubsub,
            });

            // Schedule PubSub token refresh before expiration
            if (pubsubRefreshTimerRef.current) {
              clearTimeout(pubsubRefreshTimerRef.current);
            }
            const refreshMs = Math.max((pubsub.expires_in - 300) * 1000, 60000);
            pubsubRefreshTimerRef.current = setTimeout(async () => {
              try {
                const refreshResponse = await apis.get<Auth0ProfileResponse>('/auth/profile');
                if (refreshResponse.data?.pubsub) {
                  dispatch({
                    type: 'STORE_PUBSUB_CREDENTIALS',
                    payload: refreshResponse.data.pubsub,
                  });
                }
              } catch {
                // PubSub refresh failed - non-fatal, will retry on next activity
              }
            }, refreshMs);
          }

        } else {
          throw new Error('Invalid profile response format');
        }
      } catch (err: any) {
        const status = err.response?.status;
        const errorCode = err.response?.data?.code;
        const message = readErrorMessage(err, 'Failed to fetch profile');

        if (status === 401) {
          // Any 401 during initial auth flow means the user cannot proceed
          setError(
            errorCode === 'SESSION_MISSING' || errorCode === 'SESSION_INVALID'
              ? 'Session expired. Please log in again.'
              : 'Unauthorized - please log in again.'
          );
          setTimeout(() => doLogout(), 1500);
        } else {
          // Non-401 errors (server error, network issue, etc.)
          setError(message);
          // Don't auto-logout on transient errors - let the user retry
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Cleanup PubSub refresh timer on unmount
    return () => {
      if (pubsubRefreshTimerRef.current) {
        clearTimeout(pubsubRefreshTimerRef.current);
      }
    };
  }, [isAuthenticated, isLoading, auth0User?.sub, dispatch, getAccessTokenSilently, doLogout]);

  const needsOnboarding = !loading && isAuthenticated && !profileData?.user?.onboarding_completed;

  return {
    loading,
    error,
    isAuthenticated,
    needsOnboarding,
    account,
    /** The account changed (type chosen, profile saved): render from this one. */
    setAccount,
  };
};
